import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Checkbox,
  Divider,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  Steps,
  message,
} from 'antd';
import { ArrowLeftOutlined, CheckOutlined, SaveOutlined } from '@ant-design/icons';
import hepatoApi, { CreateHepatoFormDto } from '../../api/hepatoApi';

const symptomOptions = [
  { label: 'Durere/disconfort hipocondru drept', value: 'right_abdominal_pain' },
  { label: 'Icter', value: 'jaundice' },
  { label: 'Oboseala cronica', value: 'chronic_fatigue' },
  { label: 'Pierdere in greutate', value: 'weight_loss' },
  { label: 'Scaderea apetitului', value: 'low_appetite' },
  { label: 'Greata/varsaturi', value: 'nausea_vomiting' },
  { label: 'Ascita/balonare', value: 'ascites' },
  { label: 'Prurit cutanat', value: 'pruritus' },
  { label: 'Sangerare/echimoze', value: 'bleeding_bruising' },
];

const personalHistoryOptions = [
  { label: 'Hepatita virala B', value: 'hepatitis_b' },
  { label: 'Hepatita virala C', value: 'hepatitis_c' },
  { label: 'Ciroza hepatica', value: 'cirrhosis' },
  { label: 'NAFLD', value: 'nafld' },
  { label: 'Hemocromatoza/boli genetice', value: 'genetic_liver_disease' },
  { label: 'Diabet zaharat', value: 'diabetes' },
  { label: 'Hipertensiune arteriala', value: 'hypertension' },
  { label: 'Tratamente hormonale/chimio/radioterapie', value: 'oncologic_or_hormonal_treatments' },
];

const familyHistoryOptions = [
  { label: 'Cancer hepatic', value: 'liver_cancer' },
  { label: 'Hepatita B/C', value: 'hepatitis' },
  { label: 'Ciroza hepatica', value: 'cirrhosis' },
  { label: 'Diabet zaharat', value: 'diabetes' },
];

const cancerTypeOptions = [
  { label: 'Carcinom hepatocelular', value: 'hepatocellular_carcinoma' },
  { label: 'Colangiocarcinom', value: 'cholangiocarcinoma' },
  { label: 'Metastaza hepatica', value: 'hepatic_metastasis' },
  { label: 'Altul', value: 'other' },
];

const confirmationOptions = [
  { label: 'Biopsie', value: 'biopsy' },
  { label: 'Imagistica CT/RMN/Ecografie', value: 'imaging' },
  { label: 'Marker tumoral AFP', value: 'afp' },
  { label: 'Alta metoda', value: 'other' },
];

const patientStatusOptions = [
  { label: 'Stabil', value: 'stable' },
  { label: 'Progresie', value: 'progression' },
  { label: 'Decedat', value: 'deceased' },
];

const REQUIRED_CHOICE_RULE = [{ required: true, message: 'Selectati o optiune' }];
const REQUIRED_YES_NO_RULE = [{ required: true, message: 'Selectati Da sau Nu' }];
const REQUIRED_CHECKED_RULE = [
  {
    validator: (_: unknown, value: boolean) =>
      value ? Promise.resolve() : Promise.reject(new Error('Bifati consimtamantul')),
  },
];

const SECTION_STEP_INDEX: Record<string, number> = {
  generalData: 0,
  currentSymptoms: 1,
  personalHistory: 1,
  familyHistory: 1,
  riskFactors: 2,
  previousExams: 3,
  initialDiagnosis: 3,
  diseaseStage: 4,
  labsAndImaging: 5,
  surgery: 5,
  oncologicTreatment: 5,
  treatmentResponse: 6,
  followUp: 6,
  clinicalTrials: 6,
  consent: 6,
  notes: 6,
};

const HepatoFormNewPage = () => {
  const { id: patientId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm<CreateHepatoFormDto>();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const buildDto = (values: CreateHepatoFormDto): CreateHepatoFormDto => ({
    ...values,
    patientId: patientId || '',
    programType: 'HEPATO',
  });

  const focusFirstInvalidStep = (errorFields: Array<{ name: (string | number)[] }>) => {
    const firstName = errorFields?.[0]?.name;
    const section = Array.isArray(firstName) ? String(firstName[0]) : undefined;
    const stepIndex = section ? SECTION_STEP_INDEX[section] : undefined;
    if (typeof stepIndex === 'number') {
      setCurrentStep(stepIndex);
      window.scrollTo(0, 0);
    }
  };

  const saveForm = async (submitAfterSave: boolean) => {
    if (!patientId) {
      message.error('ID pacient lipsa');
      return;
    }

    try {
      setLoading(true);
      if (submitAfterSave) {
        try {
          await form.validateFields();
        } catch (error: any) {
          focusFirstInvalidStep(error?.errorFields ?? []);
          throw error;
        }
      }
      const values = form.getFieldsValue(true);
      const savedForm = await hepatoApi.create(buildDto(values));
      const nextForm = submitAfterSave ? await hepatoApi.submit(savedForm.id) : savedForm;

      message.success(submitAfterSave ? 'Chestionar hepatic finalizat' : 'Chestionar hepatic salvat');
      navigate(`/patients/${patientId}/hepato/${nextForm.id}`);
    } catch (error: any) {
      if (error?.errorFields) {
        message.warning('Verificati campurile obligatorii din tot chestionarul');
      } else {
        message.error(error.response?.data?.message || 'Eroare la salvarea chestionarului hepatic');
      }
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      title: 'Date',
      content: (
        <>
          <Form.Item label="Greutate (kg)" name={['generalData', 'weightKg']}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Inaltime (cm)" name={['generalData', 'heightCm']}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Profesia" name={['generalData', 'profession']}>
            <Input />
          </Form.Item>
          <Form.Item label="Cod unitate medicala / sectie" name={['generalData', 'medicalUnitCode']}>
            <Input />
          </Form.Item>
          <Form.Item label="Loc de munca / expunere toxine" name={['generalData', 'occupationalExposure']}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </>
      ),
    },
    {
      title: 'Anamneza',
      content: (
        <>
          <Form.Item label="Simptome actuale" name={['currentSymptoms', 'items']}>
            <Checkbox.Group options={symptomOptions} />
          </Form.Item>
          <Form.Item label="Alte simptome" name={['currentSymptoms', 'other']}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Divider />
          <Form.Item label="Istoric medical personal" name={['personalHistory', 'conditions']}>
            <Checkbox.Group options={personalHistoryOptions} />
          </Form.Item>
          <Form.Item label="Antecedente oncologice" name={['personalHistory', 'oncologicHistory']}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="Istoric familial" name={['familyHistory', 'conditions']}>
            <Checkbox.Group options={familyHistoryOptions} />
          </Form.Item>
          <Form.Item label="Alte boli cronice familiale" name={['familyHistory', 'otherChronicDiseases']}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </>
      ),
    },
    {
      title: 'Risc',
      content: (
        <>
          <Form.Item label="Consum alcool" name={['riskFactors', 'alcoholUse']} rules={REQUIRED_CHOICE_RULE}>
            <Radio.Group>
              <Radio value="none">Nu consum</Radio>
              <Radio value="occasional">Ocazional</Radio>
              <Radio value="frequent">Frecvent</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="Cantitate alcool zilnica (ml)" name={['riskFactors', 'dailyAlcoholMl']}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Fumat" name={['riskFactors', 'smoking']} rules={REQUIRED_YES_NO_RULE}>
            <Radio.Group>
              <Radio value="no">Nu</Radio>
              <Radio value="yes">Da</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="Ani fumat" name={['riskFactors', 'smokingYears']}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Tigari/zi" name={['riskFactors', 'cigarettesPerDay']}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Expunere substante toxice/chimice" name={['riskFactors', 'toxicExposure']}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item label="Transfuzii inainte de 1992" name={['riskFactors', 'transfusionsBefore1992']} rules={REQUIRED_YES_NO_RULE}>
            <Radio.Group>
              <Radio value="no">Nu</Radio>
              <Radio value="yes">Da</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="Vaccinare hepatita B" name={['riskFactors', 'hepatitisBVaccination']} rules={REQUIRED_YES_NO_RULE}>
            <Radio.Group>
              <Radio value="no">Nu</Radio>
              <Radio value="yes">Da</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="An vaccinare" name={['riskFactors', 'hepatitisBVaccinationYear']}>
            <Input />
          </Form.Item>
          <Form.Item label="Calatorii zone risc hepatita" name={['riskFactors', 'highRiskTravelCountries']}>
            <Input />
          </Form.Item>
          <Form.Item label="Tatuaje" name={['riskFactors', 'tattoos']} rules={REQUIRED_YES_NO_RULE}>
            <Radio.Group>
              <Radio value="no">Nu</Radio>
              <Radio value="yes">Da</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="Mai mult de un partener sexual in ultimele 6 luni" name={['riskFactors', 'multipleSexualPartners']} rules={REQUIRED_YES_NO_RULE}>
            <Radio.Group>
              <Radio value="no">Nu</Radio>
              <Radio value="yes">Da</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="Consum frecvent alimente cu risc de aflatoxine" name={['riskFactors', 'aflatoxinFoodUse']} rules={REQUIRED_YES_NO_RULE}>
            <Radio.Group>
              <Radio value="no">Nu</Radio>
              <Radio value="yes">Da</Radio>
            </Radio.Group>
          </Form.Item>
        </>
      ),
    },
    {
      title: 'Diagnostic',
      content: (
        <>
          <Form.Item label="Analize functie hepatica" name={['previousExams', 'liverFunctionTests']}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item label="Data analizelor functiei hepatice" name={['previousExams', 'liverFunctionTestsDate']}>
            <Input placeholder="ZZ / LL / AAAA" />
          </Form.Item>
          <Form.Item label="Investigatii imagistice hepatice" name={['previousExams', 'imaging']}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item label="Data investigatiilor imagistice hepatice" name={['previousExams', 'imagingDate']}>
            <Input placeholder="ZZ / LL / AAAA" />
          </Form.Item>
          <Divider />
          <Form.Item label="Tip cancer hepatic" name={['initialDiagnosis', 'cancerType']}>
            <Select options={cancerTypeOptions} />
          </Form.Item>
          <Form.Item label="Alt tip cancer" name={['initialDiagnosis', 'otherCancerType']}>
            <Input />
          </Form.Item>
          <Form.Item label="Cod ICD-10" name={['initialDiagnosis', 'icd10Code']}>
            <Input />
          </Form.Item>
          <Form.Item label="Data diagnosticului" name={['initialDiagnosis', 'diagnosisDate']}>
            <Input placeholder="ZZ / LL / AAAA" />
          </Form.Item>
          <Form.Item label="Metoda confirmare" name={['initialDiagnosis', 'confirmationMethods']}>
            <Checkbox.Group options={confirmationOptions} />
          </Form.Item>
          <Form.Item label="Simptome la prezentare" name={['initialDiagnosis', 'presentationSymptoms']}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item label="Factor de risc principal" name={['initialDiagnosis', 'mainRiskFactor']}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </>
      ),
    },
    {
      title: 'Stadiu',
      content: (
        <>
          <Form.Item label="Dimensiune tumorala (cm)" name={['diseaseStage', 'tumorSizeCm']}>
            <Input />
          </Form.Item>
          <Form.Item label="Numar leziuni" name={['diseaseStage', 'lesionCount']}>
            <Input />
          </Form.Item>
          <Form.Item label="Localizare in ficat" name={['diseaseStage', 'liverLocation']}>
            <Input />
          </Form.Item>
          <Form.Item label="Infiltrare vasculara / tromboza portala" name={['diseaseStage', 'vascularInfiltration']} rules={REQUIRED_YES_NO_RULE}>
            <Radio.Group>
              <Radio value="no">Nu</Radio>
              <Radio value="yes">Da</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="Invazie ganglionara / metastaze extrahepatice" name={['diseaseStage', 'extrahepaticSpread']} rules={REQUIRED_YES_NO_RULE}>
            <Radio.Group>
              <Radio value="no">Nu</Radio>
              <Radio value="yes">Da</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="Stadiu TNM" name={['diseaseStage', 'tnmStage']}>
            <Input placeholder="T__ N__ M__" />
          </Form.Item>
          <Form.Item label="Clasificare BCLC" name={['diseaseStage', 'bclcClassification']}>
            <Input />
          </Form.Item>
        </>
      ),
    },
    {
      title: 'Medic',
      content: (
        <>
          <Form.Item label="Descriere imagistica pre tratament" name={['labsAndImaging', 'preTreatmentImagingDescription']}>
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item label="AFP initial" name={['labsAndImaging', 'initialAfp']}>
            <Input />
          </Form.Item>
          <Form.Item label="Bilirubina totala / directa" name={['labsAndImaging', 'bilirubin']}>
            <Input />
          </Form.Item>
          <Form.Item label="AST / ALT / GGT" name={['labsAndImaging', 'liverEnzymes']}>
            <Input />
          </Form.Item>
          <Form.Item label="Albumina / INR" name={['labsAndImaging', 'albuminInr']}>
            <Input />
          </Form.Item>
          <Form.Item label="Functie renala" name={['labsAndImaging', 'renalFunction']}>
            <Input />
          </Form.Item>
          <Form.Item label="Monitorizare laborator si imagistica" name={['labsAndImaging', 'followUpTable']}>
            <Input.TextArea rows={4} />
          </Form.Item>
          <Divider />
          <Form.Item label="Chirurgie / interventii" name={['surgery', 'details']}>
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item label="Tratament oncologic" name={['oncologicTreatment', 'details']}>
            <Input.TextArea rows={4} />
          </Form.Item>
        </>
      ),
    },
    {
      title: 'Follow-up',
      content: (
        <>
          <Form.Item label="Raspuns post ablatia/embolizare/TACE" name={['treatmentResponse', 'details']}>
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item label="Recidive" name={['followUp', 'recurrences']}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="Starea pacientului" name={['followUp', 'patientStatus']} rules={REQUIRED_CHOICE_RULE}>
            <Radio.Group options={patientStatusOptions} />
          </Form.Item>
          <Form.Item label="Monitorizare periodica" name={['followUp', 'periodicMonitoring']}>
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item label="Participare studii clinice" name={['clinicalTrials', 'participates']} rules={REQUIRED_YES_NO_RULE}>
            <Radio.Group>
              <Radio value="no">Nu</Radio>
              <Radio value="yes">Da</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="Detalii studiu / tratament experimental" name={['clinicalTrials', 'details']}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Divider />
          <Form.Item name={['consent', 'personalDataProcessing']} valuePropName="checked" rules={REQUIRED_CHECKED_RULE}>
            <Checkbox>Consimtamant prelucrare date personale</Checkbox>
          </Form.Item>
          <Form.Item name={['consent', 'registryInclusion']} valuePropName="checked">
            <Checkbox>Consimtamant includere registru transfrontalier</Checkbox>
          </Form.Item>
          <Form.Item name={['consent', 'futureContact']} valuePropName="checked">
            <Checkbox>Consimtamant contact follow-up</Checkbox>
          </Form.Item>
          <Form.Item label="Observatii suplimentare" name="notes">
            <Input.TextArea rows={5} />
          </Form.Item>
        </>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/patients/${patientId}`)}>
          Inapoi
        </Button>
      </Space>

      <h1>Chestionar Anamneza Tumori Hepatice</h1>

      <Steps
        current={currentStep}
        items={steps.map((step) => ({ title: step.title }))}
        style={{ marginBottom: 24 }}
      />

      <Card>
        <Form form={form} layout="vertical">
          {steps.map((step, index) => (
            <div key={step.title} style={{ display: index === currentStep ? 'block' : 'none' }}>
              {step.content}
            </div>
          ))}

          <Divider />

          <Space>
            <Button disabled={currentStep === 0} onClick={() => setCurrentStep((step) => step - 1)}>
              Inapoi
            </Button>
            <Button disabled={currentStep === steps.length - 1} onClick={() => setCurrentStep((step) => step + 1)}>
              Continua
            </Button>
            <Button icon={<SaveOutlined />} loading={loading} onClick={() => saveForm(false)}>
              Salveaza draft
            </Button>
            <Button type="primary" icon={<CheckOutlined />} loading={loading} onClick={() => saveForm(true)}>
              Finalizeaza
            </Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
};

export default HepatoFormNewPage;
